/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../styles/GoogleMapEmbed.css';

const GOOGLE_MAPS_KEY =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY) ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  '';

const buildQuery = ({ latitude, longitude, address }) => {
  if (typeof latitude === 'number' && typeof longitude === 'number') {
    return `${latitude},${longitude}`;
  }

  if (latitude && longitude) {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return `${lat},${lng}`;
    }
  }

  if (address && address.trim()) {
    return address.trim();
  }

  return null;
};

const GoogleMapEmbed = ({
  address,
  latitude,
  longitude,
  zoom = 15,
  height = 260,
  label,
  className = '',
}) => {
  const [apiError, setApiError] = useState(false);
  const query = useMemo(
    () => buildQuery({ latitude, longitude, address }),
    [address, latitude, longitude]
  );

  useEffect(() => {
    // Reset error when query changes
    setApiError(false);
  }, [query]);

  if (!query) {
    return null;
  }

  if (!GOOGLE_MAPS_KEY) {
    return (
      <div className={`map-embed map-embed--missing-key ${className}`}>
        <div className="map-embed__fallback">
          <span>Google Maps preview unavailable.</span>
          <span className="map-embed__hint">
            Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> or <code>VITE_GOOGLE_MAPS_API_KEY</code> in your frontend environment to enable the map.
          </span>
        </div>
      </div>
    );
  }

  const encodedQuery = encodeURIComponent(query);
  const src = `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${encodedQuery}&zoom=${zoom}`;

  const handleIframeError = () => {
    setApiError(true);
  };

  if (apiError) {
    return (
      <div className={`map-embed map-embed--missing-key ${className}`}>
        <div className="map-embed__fallback">
          <span>Google Maps preview unavailable.</span>
          <span className="map-embed__hint">
            API key authorization error. Please ensure:
            <br />• Maps Embed API is enabled in Google Cloud Console
            <br />• Billing is enabled for your Google Cloud project
            <br />• API key restrictions allow this domain
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`map-embed ${className}`} style={{ minHeight: height }}>
      {label && <div className="map-embed__label">{label}</div>}
      <iframe
        title={`Google Maps preview for ${query}`}
        src={src}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        onError={handleIframeError}
        onLoad={(e) => {
          // Check if iframe loaded an error page
          try {
            const iframe = e.target;
            if (iframe.contentDocument && iframe.contentDocument.body) {
              const bodyText = iframe.contentDocument.body.innerText || '';
              if (bodyText.includes('rejected') || bodyText.includes('not authorized') || bodyText.includes('API key')) {
                setApiError(true);
              }
            }
          } catch (err) {
            // Cross-origin restrictions may prevent access
            // Error will be caught by onError if it's a real issue
          }
        }}
      />
    </div>
  );
};

GoogleMapEmbed.propTypes = {
  address: PropTypes.string,
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  zoom: PropTypes.number,
  height: PropTypes.number,
  label: PropTypes.string,
  className: PropTypes.string,
};

export default GoogleMapEmbed;

